import { Classes, Dialog, FormGroup, InputGroup, MenuItem } from "@blueprintjs/core";
import { ItemRenderer } from "@blueprintjs/select";
import classnames from "classnames";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    endKey,
    LINK_TYPE_LABELS,
    LINK_TYPE_NOTES,
    LINK_TYPE_RULES,
    LINK_TYPE_TITLES,
    LINK_TYPE_USAGE,
    lagNote,
    linkCaption,
    linkSentence,
    PlanLinkEndInput,
    PlanLinkEndKind,
    PlanLinkEndResponse,
    PlanLinkResponse,
    PlanLinkTargets,
    PlanLinkType,
} from "../models/plan-dependencies";
import { LinkTypeDiagram } from "./LinkTypeDiagram";
import styles from "./LinksDialog.module.scss";

/** Объект, чьи связи показывает окно: работа или раздел */
export interface LinksTarget {
    kind: PlanLinkEndKind;
    orderId: string;
    assignmentId?: string | null;
    contentGuid?: string | null;
    /** «0522 · АР · Богданова Е.В.» */
    label: string;
    /** Можно добавлять связи от этого объекта */
    canEdit: boolean;
}

interface LinksDialogProps {
    target: LinksTarget;
    /** Все связи графика — окно выберет свои */
    links: PlanLinkResponse[];
    onSave: (link: {
        id?: string | null;
        type: PlanLinkType;
        lag: number;
        from?: PlanLinkEndInput;
        to?: PlanLinkEndInput;
    }) => Promise<PlanLinkResponse | null>;
    onRemove: (id: string) => Promise<boolean>;
    onSearch: (query: string, orderId: string | null, kind: PlanLinkEndKind) => Promise<PlanLinkTargets | null>;
    onClose: () => void;
}

/** Типы связи для селекта */
const TYPE_OPTIONS: SelectItem[] = (["fs", "ss", "ff", "sf"] as PlanLinkType[]).map((type) => ({
    id: type,
    label: LINK_TYPE_LABELS[type],
}));

const DIRECTION_OPTIONS: SelectItem[] = [
    { id: "out", label: "От этого объекта — он предшественник" },
    { id: "in", label: "К этому объекту — он последователь" },
];

/** Пауза поиска проекта после ввода, мс */
const SEARCH_DELAY = 300;

/**
 * Пункт списка типов: схема (А — предшественник, Б — последователь), название и что значит.
 * Выбирают глазами по схеме, а не по двум буквам
 */
const renderTypeOption: ItemRenderer<SelectItem> = (item, { handleClick, modifiers }) => {
    const type = item.id as PlanLinkType;

    return (
        <MenuItem
            key={item.id}
            active={modifiers.active}
            onClick={handleClick}
            multiline={true}
            className={styles.typeOption}
            icon={<LinkTypeDiagram type={type} />}
            text={
                <div>
                    <div className={styles.typeTitle}>
                        <b>{LINK_TYPE_LABELS[type]}</b> — {LINK_TYPE_TITLES[type]}
                    </div>
                    <div className={styles.typeNote}>{LINK_TYPE_RULES[type]}</div>
                </div>
            }
        />
    );
};

/** Список типов шире кнопки: схема и две строки пояснения в узкую колонку не влезают */
const TYPE_POPOVER = { minimal: true, placement: "bottom-start" as const, popoverClassName: styles.typePopover };

const typeOption = (type: PlanLinkType) => TYPE_OPTIONS.find((item) => item.id === type) || TYPE_OPTIONS[0];

/**
 * Свойства полосы — её связи (п. 6 ТЗ): входящие и исходящие с проектами, тип и лаг
 * меняются на месте, связь удаляется. Ниже — новая связь: второй конец ищется по всем
 * проектам, а не только попавшим в график (у раздела — среди разделов того же проекта).
 * Вид — как у остальных окон приложения: figma-dialog, planr-поля, SimpleSelect.
 */
export const LinksDialog: React.FC<LinksDialogProps> = (props) => {
    const { target, links, onSave, onRemove, onSearch, onClose } = props;

    const key = endKey({
        ...target,
        assignmentId: target.assignmentId || null,
        contentGuid: target.contentGuid || null,
    });

    const own = useMemo(
        () => links.filter((link) => (link.from && endKey(link.from) === key) || (link.to && endKey(link.to) === key)),
        [links, key]
    );

    const incoming = own.filter((link) => link.to && endKey(link.to) === key);
    const outgoing = own.filter((link) => link.from && endKey(link.from) === key);

    const [busy, setBusy] = useState(false);

    const run = useCallback(async (action: () => Promise<unknown>) => {
        setBusy(true);

        try {
            await action();
        } finally {
            setBusy(false);
        }
    }, []);

    /* ---------- новая связь ---------- */

    /** Этот объект — предшественник (исходящая связь) или последователь (входящая) */
    const [direction, setDirection] = useState<"out" | "in">("out");
    const [type, setType] = useState<PlanLinkType>("fs");
    const [lag, setLag] = useState(0);

    const [query, setQuery] = useState("");
    const [orders, setOrders] = useState<{ id: string; label: string }[]>([]);
    /** Проект второго конца; у раздела — тот же проект, выбирать нечего */
    const [order, setOrder] = useState<{ id: string; label: string } | null>(
        target.kind === "section" ? { id: target.orderId, label: "" } : null
    );
    const [targets, setTargets] = useState<PlanLinkEndResponse[]>([]);
    const [picked, setPicked] = useState<PlanLinkEndResponse | null>(null);

    // проекты — по вводу, с паузой
    useEffect(() => {
        if (!target.canEdit || target.kind === "section" || order) {
            return;
        }

        const timer = window.setTimeout(async () => {
            const found = await onSearch(query, null, target.kind);
            setOrders(found ? found.orders : []);
        }, SEARCH_DELAY);

        return () => window.clearTimeout(timer);
    }, [query, order, target.canEdit, target.kind, onSearch]);

    // объекты выбранного проекта: работы сотрудников или разделы
    useEffect(() => {
        if (!target.canEdit || !order) {
            setTargets([]);
            return;
        }

        let alive = true;

        onSearch("", order.id, target.kind).then((found) => {
            if (alive) {
                // сам объект вторым концом не бывает
                setTargets(found ? found.targets.filter((item) => endKey(item) !== key) : []);
            }
        });

        return () => {
            alive = false;
        };
    }, [order, target.canEdit, target.kind, onSearch, key]);

    const add = () =>
        picked &&
        run(async () => {
            const self: PlanLinkEndInput = {
                kind: target.kind,
                orderId: target.orderId,
                assignmentId: target.assignmentId || null,
                contentGuid: target.contentGuid || null,
            };

            const other: PlanLinkEndInput = {
                kind: picked.kind,
                orderId: picked.orderId,
                assignmentId: picked.assignmentId,
                contentGuid: picked.contentGuid,
            };

            const saved = await onSave({
                type,
                lag,
                from: direction === "out" ? self : other,
                to: direction === "out" ? other : self,
            });

            if (saved) {
                setPicked(null);
                setLag(0);
                setType("fs");
            }
        });

    const renderLink = (link: PlanLinkResponse, other: PlanLinkEndResponse | null) => (
        <div key={link.id} className={styles.link}>
            <div className={styles.other} title={other ? other.label : ""}>
                {other ? other.label : "—"}
            </div>

            <div
                className={styles.type}
                data-tip={`${linkCaption(link.type, link.lag)} — ${LINK_TYPE_TITLES[link.type]}`}
                data-tip-note={linkSentence(
                    link.type,
                    link.lag,
                    link.from ? link.from.label : "",
                    link.to ? link.to.label : ""
                )}
            >
                <SimpleSelect
                    className={`full-width-select ${Classes.FILL}`}
                    filterable={false}
                    disabled={!link.canEdit || busy}
                    activeItem={typeOption(link.type)}
                    items={TYPE_OPTIONS}
                    itemRenderer={renderTypeOption}
                    matchTargetWidth={false}
                    popoverProps={TYPE_POPOVER}
                    onItemSelect={(item: SelectItem) => {
                        if (item.id !== link.type) {
                            run(() => onSave({ id: link.id, type: item.id as PlanLinkType, lag: link.lag }));
                        }
                    }}
                >
                    <DefaultSelectedOption option={typeOption(link.type)} disabled={!link.canEdit || busy} />
                </SimpleSelect>
            </div>

            <LagInput
                value={link.lag}
                disabled={!link.canEdit || busy}
                onCommit={(value) => run(() => onSave({ id: link.id, type: link.type, lag: value }))}
            />

            <div className={styles.remove}>
                {link.canEdit && (
                    <RemoveConfirmation<PlanLinkResponse>
                        what={() => `связь с «${other ? other.label : ""}»`}
                        onConfirmed={() => run(() => onRemove(link.id))}
                    >
                        {({ confirmRemoving }) => (
                            <GeneralIcon
                                type="general-trash"
                                title="Удалить связь"
                                className={styles.trash}
                                onClick={() => confirmRemoving(link)}
                            />
                        )}
                    </RemoveConfirmation>
                )}
            </div>
        </div>
    );

    const renderGroup = (
        caption: string,
        items: PlanLinkResponse[],
        otherOf: (link: PlanLinkResponse) => PlanLinkEndResponse | null
    ) => (
        <div className={styles.group}>
            <div className={styles.caption}>{caption}</div>
            {items.length ? (
                <>
                    <div className={styles.head}>
                        <span className={styles.other}>Связанный объект</span>
                        <span
                            className={classnames(styles.type, styles.hint)}
                            data-tip="Тип связи"
                            data-tip-note={(["fs", "ss", "ff", "sf"] as PlanLinkType[])
                                .map((item) => `${LINK_TYPE_LABELS[item]} — ${LINK_TYPE_NOTES[item]}`)
                                .join("\n")}
                        >
                            Тип
                        </span>
                        <span
                            className={classnames(styles.lagHead, styles.hint)}
                            data-tip="Лаг, рабочих дней"
                            data-tip-note={"Плюс — пауза между работами, минус — нахлёст (можно раньше), 0 — вплотную"}
                        >
                            Лаг
                        </span>
                        <span className={styles.remove} />
                    </div>
                    {items.map((link) => renderLink(link, otherOf(link)))}
                </>
            ) : (
                <div className={styles.empty}>Нет</div>
            )}
        </div>
    );

    const directionOption = DIRECTION_OPTIONS.find((item) => item.id === direction) || DIRECTION_OPTIONS[0];

    return (
        <Dialog
            isOpen={true}
            onClose={onClose}
            title="Связи"
            isCloseButtonShown={true}
            backdropClassName="standard"
            enforceFocus={false}
            className={`figma-dialog ${styles.dialog}`}
        >
            <div className={Classes.DIALOG_BODY}>
                <div className={styles.subject}>{target.label}</div>

                {target.canEdit && (
                    <div className={styles.add}>
                        <div className={styles.caption}>Новая связь</div>

                        <FormGroup label="Направление" inline={true} className="planr-form-input">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={directionOption}
                                items={DIRECTION_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => setDirection(item.id as "out" | "in")}
                            >
                                <DefaultSelectedOption option={directionOption} />
                            </SimpleSelect>
                        </FormGroup>

                        {target.kind === "work" && (
                            <FormGroup label="Проект" inline={true} className="planr-form-input">
                                {order ? (
                                    <div className={styles.chosen}>
                                        <span>{order.label}</span>
                                        <GeneralIcon
                                            type="general-cross-small"
                                            title="Выбрать другой проект"
                                            className={styles.clear}
                                            onClick={() => {
                                                setOrder(null);
                                                setPicked(null);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <InputGroup
                                            className="planr-default-input"
                                            autoFocus={true}
                                            value={query}
                                            placeholder="Номер или название"
                                            autoComplete="off"
                                            onChange={(event) => setQuery(event.target.value)}
                                        />
                                        <div className={styles.list}>
                                            {orders.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className={styles.item}
                                                    onClick={() => setOrder(item)}
                                                >
                                                    {item.label}
                                                </div>
                                            ))}
                                            {!orders.length && <div className={styles.empty}>Проекты не найдены</div>}
                                        </div>
                                    </>
                                )}
                            </FormGroup>
                        )}

                        {order && (
                            <FormGroup
                                label={target.kind === "section" ? "Раздел" : "Работа"}
                                inline={true}
                                className="planr-form-input"
                            >
                                <div className={styles.list}>
                                    {targets.map((item) => (
                                        <div
                                            key={endKey(item)}
                                            className={classnames(styles.item, {
                                                [styles.itemPicked]: !!picked && endKey(picked) === endKey(item),
                                            })}
                                            onClick={() => setPicked(item)}
                                        >
                                            <span>{item.label}</span>
                                            <span className={styles.dates}>
                                                {item.start && item.end
                                                    ? `${short(item.start)}–${short(item.end)}`
                                                    : ""}
                                            </span>
                                        </div>
                                    ))}
                                    {!targets.length && (
                                        <div className={styles.empty}>
                                            {target.kind === "section" ? "Других разделов с работами нет" : "Работ нет"}
                                        </div>
                                    )}
                                </div>
                            </FormGroup>
                        )}

                        <div className={styles.row}>
                            <FormGroup
                                label="Тип связи"
                                inline={true}
                                className={`planr-form-input ${styles.typeField}`}
                            >
                                <SimpleSelect
                                    className={`full-width-select ${Classes.FILL}`}
                                    filterable={false}
                                    activeItem={typeOption(type)}
                                    items={TYPE_OPTIONS}
                                    itemRenderer={renderTypeOption}
                                    matchTargetWidth={false}
                                    popoverProps={TYPE_POPOVER}
                                    onItemSelect={(item: SelectItem) => setType(item.id as PlanLinkType)}
                                >
                                    <DefaultSelectedOption option={typeOption(type)} />
                                </SimpleSelect>
                            </FormGroup>

                            <FormGroup label="Лаг, раб. дн." inline={true} className="planr-form-input">
                                <LagInput value={lag} disabled={false} onCommit={setLag} />
                            </FormGroup>
                        </div>

                        {/* связь словами на выбранных объектах — видно, что получится, до «Добавить» */}
                        <div className={styles.hintBox}>
                            <LinkTypeDiagram type={type} />
                            <div className={styles.hintText}>
                                <div className={styles.sentence}>
                                    {linkSentence(
                                        type,
                                        lag,
                                        direction === "out" ? target.label : picked ? picked.label : "второй объект",
                                        direction === "out" ? (picked ? picked.label : "второй объект") : target.label
                                    )}
                                </div>
                                <div className={styles.note}>Лаг {lagNote(lag)}</div>
                                <div className={styles.note}>Когда выбирать: {LINK_TYPE_USAGE[type]}</div>
                            </div>
                        </div>
                    </div>
                )}
                {renderGroup("Входящие — этот объект ждёт", incoming, (link) => link.from)}
                {renderGroup("Исходящие — от этого объекта зависят", outgoing, (link) => link.to)}
            </div>

            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    {target.canEdit && (
                        <PlanrButton type="greenish" size="small" disabled={!picked || busy} onClick={add}>
                            Добавить связь
                        </PlanrButton>
                    )}
                    <PlanrButton type="graybtn" size="small" onClick={onClose}>
                        Закрыть
                    </PlanrButton>
                </div>
            </div>
        </Dialog>
    );
};

/** Лаг: правится на месте, уходит по Enter или потере фокуса. Плюс — задержка, минус — опережение */
const LagInput: React.FC<{ value: number; disabled: boolean; onCommit: (value: number) => void }> = ({
    value,
    disabled,
    onCommit,
}) => {
    const [text, setText] = useState(String(value));

    // сервер вернул другое значение (или откатил) — показываем его
    useEffect(() => setText(String(value)), [value]);

    const commit = () => {
        const next = parseInt(text, 10);

        if (isNaN(next)) {
            setText(String(value));
        } else if (next !== value) {
            onCommit(next);
        }
    };

    return (
        <InputGroup
            className={`planr-default-input ${styles.lag}`}
            value={text}
            disabled={disabled}
            inputMode="numeric"
            autoComplete="off"
            title="Лаг в рабочих днях: плюс — задержка, минус — опережение"
            onChange={(event) => setText(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => event.key === "Enter" && commit()}
        />
    );
};

/** yyyy-MM-dd → дд.мм */
function short(day: string): string {
    return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}
